import React, {useEffect, useState} from "react";
import {Redirect, Route, useParams} from "react-router-dom";
import AdminNav from "components/Admin/AdminNav";
import {Container} from "react-bootstrap";
import {DbCollectionFactory} from "factories/DbCollectionFactory";
import {DB_COLLECTION_NAMES, PAGE_URLS} from "constants/AppConstants";
import {useLayout} from "contexts/LayoutContext";
import WaitingModal from "components/Common/WaitingModal";
import {StyleDiv, StyleDivCenterChild} from "components/Common/StyleDiv";
import {StyleLabelTitle} from "components/Common/StyleLabel";
import {ClipLoader} from "react-spinners";
import {useAuth} from "contexts/AuthContext";
import {LOADER_SIZE} from "constants/StyleConstants.js";
import {ws} from "styles/tokens";
import {RingLoader} from "react-spinners";
import {useTranslation} from "react-i18next";

interface AdminRouteProps {
  component: React.ComponentType<any>;
  [key: string]: any;
}

const AdminRoute = ({component: Component, ...rest}: AdminRouteProps) => {
  return <Route {...rest} render={(props) => <AdminLayout component={Component} {...props} {...rest.props} />} />;
};

interface AdminLayoutProps {
  component: React.ComponentType<any>;
  userNeeded?: boolean;
  [key: string]: any;
}

function AdminLayout({component: Component, userNeeded = true, ...rest}: AdminLayoutProps) {
  const {t} = useTranslation();
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const {user, isAuthenticating, claims, isGettingClaims} = useAuth();

  const {userId} = useParams<{userId: string}>();
  const {showLoader, setShowLoader, showWaitingModal, waitingModalText} = useLayout();
  const [isUserLoaded, setIsUserLoaded] = useState(false);

  useEffect(() => {
    if (!userId) {
      return;
    }

    let cancelled = false;
    setIsUserLoaded(false);
    setShowLoader(true);

    (async () => {
      const userToSearch = await getUser(userId);
      if (cancelled) {
        return;
      }
      setSelectedUser(userToSearch);
      setShowLoader(false);
      setIsUserLoaded(true);
    })();

    return () => {
      cancelled = true;
      setShowLoader(false);
    };
  }, [userId, setShowLoader]);

  const isUserReady = !userNeeded || (isUserLoaded && selectedUser?.uid === userId);

  const getUser = async (userUidParam: string) => {
    const userCollection = DbCollectionFactory.create(DB_COLLECTION_NAMES.users) as any;
    const userToSearch = await userCollection.getUserByIdAsync(userUidParam, true);

    if (userToSearch) {
      userToSearch.uid = userUidParam;
    }

    return userToSearch;
  };

  if (isAuthenticating || isGettingClaims)
    return (
      <StyleDivCenterChild height="100vh">
        <RingLoader loading={true} color={ws.accent} size={LOADER_SIZE} speedMultiplier={0.5} />
      </StyleDivCenterChild>
    );

  if (!user) {
    return <Route render={() => <Redirect to="/sign-in" />} />;
  }

  if (!claims || (!claims.admin && !claims.supervisor)) {
    return <Redirect to={PAGE_URLS.unauthorizedPageUrl} />;
  }

  if (!isUserReady && userNeeded) {
    return (
      <StyleDivCenterChild height="100vh">
        <ClipLoader loading={true} color={ws.accent} size={30} speedMultiplier={0.5} />
      </StyleDivCenterChild>
    );
  }

  if (!selectedUser && isUserLoaded && userNeeded) {
    return <Redirect to={PAGE_URLS.adminPageUrl} />;
  }

  return (
    <Container fluid style={{backgroundColor: ws.wash, color: ws.fg, minHeight: "100vh"}}>
      <AdminNav selectedUser={selectedUser} setSelectedUser={setSelectedUser} />
      <StyleDiv display={showLoader ? "block" : "none"}>
        <StyleDivCenterChild height="calc(100vh - 70px)">
          <ClipLoader loading={true} color={ws.accent} size={30} speedMultiplier={0.5} />
        </StyleDivCenterChild>
      </StyleDiv>

      <StyleDiv backgroundColor={ws.wash} display={showLoader ? "none" : "block"}>
        {selectedUser || !userNeeded ? (
          <Component selectedUser={selectedUser} setSelectedUser={setSelectedUser} {...rest} />
        ) : (
          <StyleDivCenterChild>
            <StyleLabelTitle
              fontSize={"2rem"}
              fontWeight="400"
              color={ws.fg}
              style={{fontFamily: ws.fontDisplay, letterSpacing: "-0.02em"}}
            >
              {t("admin.selectValidUserToViewActivities")}
            </StyleLabelTitle>
          </StyleDivCenterChild>
        )}
      </StyleDiv>
      <WaitingModal show={showWaitingModal} text={waitingModalText} />
    </Container>
  );
}

export default AdminRoute;
